/**
 * The teacher's shared standards default, held by Objectives, as a Planner course. Pure.
 *
 * Objectives stores one partial default per teacher and every Labs tool merges its picks
 * into it (KTD7). Planner reads it only to prefill New plan and writes it only when a plan
 * is made; opening a plan touches neither (R8). Copied from One Step Ahead's core (KTD6).
 */
import { GRADE_LEVELS, STATES, SUBJECTS, type Course } from './course';

/** The three fields as Objectives carries them: any may be missing. */
export interface StandardsFields {
  jurisdiction?: string;
  academicSubject?: string;
  gradeLevels?: string[];
}

/** What `GET /api/standards-default` on Objectives answers with, when there is a default. */
export interface SharedDefault extends StandardsFields {
  updatedAt: string;
  updatedByTool: string | null;
}

/** A PATCH body. `matchedPrefill` says the teacher submitted the prefilled values unchanged. */
export interface StandardsPick extends StandardsFields {
  matchedPrefill?: boolean;
}

/** A course with any field missing: what New plan opens with. */
export type CoursePrefill = Partial<Course>;

/** Null, `''` and `[]` count as not sent, as they do in Objectives' PATCH. */
export function nonEmptyFields(fields: StandardsFields): StandardsFields {
  return {
    ...(typeof fields.jurisdiction === 'string' && fields.jurisdiction !== '' ? { jurisdiction: fields.jurisdiction } : {}),
    ...(typeof fields.academicSubject === 'string' && fields.academicSubject !== '' ? { academicSubject: fields.academicSubject } : {}),
    ...(Array.isArray(fields.gradeLevels) && fields.gradeLevels.length > 0 ? { gradeLevels: [...fields.gradeLevels] } : {}),
  };
}

/**
 * The default as a New plan prefill (R6, R7).
 *
 * A value Planner does not offer is dropped, not refused, so a subject only another tool
 * allows (`Other`) still leaves the state and grade in place. A plan has one grade, so of
 * several the lowest Planner offers is taken, in Planner's own order: [PK, K] gives K.
 */
export function prefillCourse(shared: StandardsFields | null): CoursePrefill {
  if (!shared) return {};
  const state = STATES.find(s => s === shared.jurisdiction);
  const subject = SUBJECTS.find(s => s === shared.academicSubject);
  const grades = Array.isArray(shared.gradeLevels) ? shared.gradeLevels : [];
  const grade = GRADE_LEVELS.find(g => grades.includes(g));
  return { ...(state ? { state } : {}), ...(subject ? { subject } : {}), ...(grade ? { grade } : {}) };
}

/** Whether the teacher submitted the prefill unchanged: equal on all three fields. */
export function courseMatchesPrefill(prefill: CoursePrefill, course: Course): boolean {
  return prefill.state === course.state && prefill.grade === course.grade && prefill.subject === course.subject;
}

/**
 * The PATCH for a plan just made: its one grade replaces the stored grades (AE2).
 * `matchedPrefill` is untrusted body input and is sent only when it is a boolean.
 */
export function pickFromCourse(course: Course, matchedPrefill: unknown): StandardsPick {
  return {
    jurisdiction: course.state,
    academicSubject: course.subject,
    gradeLevels: [course.grade],
    ...(typeof matchedPrefill === 'boolean' ? { matchedPrefill } : {}),
  };
}

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

/** A default's fields from untrusted input; fields of the wrong type are dropped. */
function fieldsOf(raw: Record<string, unknown>): StandardsFields {
  return {
    ...(typeof raw.jurisdiction === 'string' ? { jurisdiction: raw.jurisdiction } : {}),
    ...(typeof raw.academicSubject === 'string' ? { academicSubject: raw.academicSubject } : {}),
    ...(Array.isArray(raw.gradeLevels) && raw.gradeLevels.every(g => typeof g === 'string') ? { gradeLevels: raw.gradeLevels as string[] } : {}),
  };
}

/**
 * The body of Objectives' GET and PATCH answers, read as untrusted input. Anything that is
 * not `{ default: { ..., updatedAt } }` is no default.
 */
export function parseSharedDefault(body: unknown): SharedDefault | null {
  if (!isRecord(body) || !isRecord(body.default)) return null;
  const raw = body.default;
  if (typeof raw.updatedAt !== 'string') return null;
  return { ...fieldsOf(raw), updatedAt: raw.updatedAt, updatedByTool: typeof raw.updatedByTool === 'string' ? raw.updatedByTool : null };
}

/**
 * The browser tests' fixed default, from the e2e-only `STANDARDS_DEFAULT_E2E` JSON: just the
 * three fields. Null when it is not a JSON object.
 */
export function parseE2eDefault(text: string): SharedDefault | null {
  try {
    const raw: unknown = JSON.parse(text);
    return isRecord(raw) ? { ...fieldsOf(raw), updatedAt: '1970-01-01T00:00:00.000Z', updatedByTool: null } : null;
  } catch {
    return null;
  }
}
